import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faCalendarAlt, 
  faChartLine,
  faUsers,
  faEye,
  faDollarSign,
  faFilter,
  faSpinner,
  faExclamationTriangle,
  faLink
} from '@fortawesome/free-solid-svg-icons';

function AffiliateReports() {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const [reportData, setReportData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Filter states
  const [selectedSalesperson, setSelectedSalesperson] = useState('');
  const [selectedReferralCode, setSelectedReferralCode] = useState('');
  const [filterOptions, setFilterOptions] = useState({
    salespersons: [],
    codes: []
  });

  // Check if user is affiliate admin
  const isAffiliateAdmin = currentUser?.isAffiliateAdmin === 'True' || currentUser?.isAffiliateAdmin === true;

  useEffect(() => {
    if (currentUser) {
      fetchFilterOptions();
      fetchReportData();
    }
  }, [currentUser]);

  const fetchFilterOptions = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateFilterOptions',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFilterOptions({
          salespersons: data.salespersons || [],
          codes: data.codes || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      
      // Fetch transaction reports
      const reportsResponse = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateTransactionReports',
          payload: {
            userId: currentUser.userId,
            startDate,
            endDate,
            referralCode: selectedReferralCode || null,
            salespersonName: selectedSalesperson || null
          }
        })
      });

      if (!reportsResponse.ok) {
        throw new Error('Failed to fetch transaction reports');
      }

      const reportsData = await reportsResponse.json();

      // Fetch commission summary
      const summaryResponse = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateCommissionSummary',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      if (!summaryResponse.ok) {
        throw new Error('Failed to fetch commission summary');
      }

      const summaryData = await summaryResponse.json();

      setReportData(reportsData);
      setSummaryData(summaryData);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeSubmit = (e) => {
    e.preventDefault();
    fetchReportData();
  };

  const exportReport = (format) => {
    if (!reportData?.data || reportData.data.length === 0) {
      alert('No data to export');
      return;
    }

    if (format === 'csv') {
      exportToCsv();
    } else if (format === 'pdf') {
      exportToPdf();
    }
  };

  const exportToCsv = () => {
    const headers = [
      'Transaction Date',
      'Location Name',
      'City',
      'State',
      'Company',
      ...(isAffiliateAdmin ? ['Referral Code'] : []),
      'Product',
      'Quantity',
      'User PPU',
      'Cost PPU',
      'Margin per Unit',
      'Total Margin',
      'Affiliate Rate %',
      ...(isAffiliateAdmin ? ['Salesperson Rate %', 'Total Commission', 'Salesperson Commission', 'Affiliate Profit'] : ['Commission Earned'])
    ];

    const csvContent = [
      headers.join(','),
      ...reportData.data.map(row => [
        row.transactionDate,
        `"${row.locationName || ''}"`,
        `"${row.locationCity || ''}"`,
        `"${row.locationState || ''}"`,
        `"${row.companyName || ''}"`,
        ...(isAffiliateAdmin ? [`"${row.referralCode || ''}"`] : []),
        `"${row.product || ''}"`,
        row.quantity,
        row.userPPU.toFixed(4),
        row.costPPU.toFixed(4),
        row.margin.toFixed(4),
        row.marginTotal.toFixed(2),
        (row.affiliateCommissionRate * 100).toFixed(1),
        ...(isAffiliateAdmin ? [
          (row.salespersonCommissionRate * 100).toFixed(1),
          row.totalCommissionEarned.toFixed(2),
          row.salespersonCommissionEarned.toFixed(2),
          row.affiliateProfitEarned.toFixed(2)
        ] : [row.commissionEarned.toFixed(2)])
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `affiliate-commission-report-${startDate}-to-${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPdf = () => {
    // This would integrate with a PDF library like jsPDF
    alert('PDF export functionality will be implemented');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl mb-3 animate-spin" style={{ color: theme.primaryColor }} />
          <p>Loading commission reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-3" />
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Reports</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <button
                onClick={fetchReportData}
                className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 
            className="text-2xl lg:text-3xl font-bold"
            style={{ color: theme.textPrimary || '#111827' }}
          >
            Commission Reports
          </h1>
          <p 
            className="text-sm lg:text-base"
            style={{ color: theme.textSecondary || '#6b7280' }}
          >
            {isAffiliateAdmin ? 'View commission earnings across all team referrals' : 'View commission earnings from your referral codes'}
          </p>
          {!isAffiliateAdmin && (
            <div className="mt-2">
              <span 
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                Team Member View
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={() => exportReport('csv')}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            disabled={!reportData?.data || reportData.data.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => exportReport('pdf')}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            disabled={!reportData?.data || reportData.data.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div 
        className="rounded-lg shadow p-4 lg:p-6"
        style={{ backgroundColor: theme.cardBG || '#ffffff' }}
      >
        <h2 
          className="text-lg font-semibold mb-4"
          style={{ color: theme.textPrimary || '#111827' }}
        >
          <FontAwesomeIcon icon={faFilter} className="mr-2" />
          Date Range Filter
        </h2>
        <form onSubmit={handleDateRangeSubmit} className="space-y-4">
          {/* Date Range Row */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="flex-1">
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
          </div>

          {/* Filters Row (Admin Only) */}
          {isAffiliateAdmin && filterOptions.salespersons.length > 0 && (
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex-1">
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Filter by Salesperson
                </label>
                <select
                  value={selectedSalesperson}
                  onChange={(e) => setSelectedSalesperson(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white"
                >
                  <option value="">All Salespersons</option>
                  {filterOptions.salespersons.map((salesperson) => (
                    <option key={salesperson} value={salesperson}>
                      {salesperson}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Filter by Referral Code
                </label>
                <select
                  value={selectedReferralCode}
                  onChange={(e) => setSelectedReferralCode(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white"
                >
                  <option value="">All Referral Codes</option>
                  {filterOptions.codes.map((codeData) => (
                    <option key={codeData.code} value={codeData.code}>
                      {codeData.code} {codeData.salesperson && `(${codeData.salesperson})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Member-specific code filter */}
          {!isAffiliateAdmin && filterOptions.codes.length > 1 && (
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex-1">
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Filter by Your Referral Code
                </label>
                <select
                  value={selectedReferralCode}
                  onChange={(e) => setSelectedReferralCode(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white"
                >
                  <option value="">All Your Codes</option>
                  {filterOptions.codes.map((codeData) => (
                    <option key={codeData.code} value={codeData.code}>
                      {codeData.code} - {codeData.campaign}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1"></div> {/* Spacer for alignment */}
            </div>
          )}

          {/* Submit Button and Clear Filters */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 items-end">
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2 text-white rounded-md hover:opacity-90"
              style={{ backgroundColor: theme.primaryColor || '#3b82f6' }}
            >
              Update Report
            </button>
            {(selectedSalesperson || selectedReferralCode) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedSalesperson('');
                  setSelectedReferralCode('');
                }}
                className="w-full sm:w-auto px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Clear Filters
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Summary Cards */}
      {reportData?.summary && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isAffiliateAdmin ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-4`}>
          <div 
            className="rounded-lg shadow p-4 lg:p-6"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="flex items-center">
              <FontAwesomeIcon 
                icon={faUsers} 
                className="text-xl lg:text-2xl mr-3" 
                style={{ color: theme.primaryColor || '#3b82f6' }}
              />
              <div>
                <div 
                  className="text-xl lg:text-2xl font-bold"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  {reportData.summary.totalTransactions}
                </div>
                <div 
                  className="text-sm"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Total Transactions
                </div>
              </div>
            </div>
          </div>
          
          <div 
            className="rounded-lg shadow p-4 lg:p-6"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="flex items-center">
              <FontAwesomeIcon 
                icon={faChartLine} 
                className="text-xl lg:text-2xl mr-3"
                style={{ color: '#10b981' }}
              />
              <div>
                <div 
                  className="text-xl lg:text-2xl font-bold"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  ${reportData.summary.totalMargin.toFixed(2)}
                </div>
                <div 
                  className="text-sm"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Total Margin
                </div>
              </div>
            </div>
          </div>
          
          <div 
            className="rounded-lg shadow p-4 lg:p-6"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="flex items-center">
              <FontAwesomeIcon 
                icon={faDollarSign} 
                className="text-xl lg:text-2xl mr-3"
                style={{ color: '#059669' }}
              />
              <div>
                <div 
                  className="text-xl lg:text-2xl font-bold"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  ${(reportData.summary.totalCommissionPaid || reportData.summary.totalCommissionEarned).toFixed(2)}
                </div>
                <div 
                  className="text-sm"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  {isAffiliateAdmin ? 'Total Commission Paid' : 'Commission Earned'}
                </div>
              </div>
            </div>
          </div>
          
          <div 
            className="rounded-lg shadow p-4 lg:p-6"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="flex items-center">
              <FontAwesomeIcon 
                icon={faCalendarAlt} 
                className="text-xl lg:text-2xl mr-3"
                style={{ color: '#f59e0b' }}
              />
              <div>
                <div 
                  className="text-xl lg:text-2xl font-bold"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  ${reportData.summary.averageCommissionPerTransaction.toFixed(2)}
                </div>
                <div 
                  className="text-sm"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Avg per Transaction
                </div>
              </div>
            </div>
          </div>
          
          {/* Admin-only additional cards */}
          {isAffiliateAdmin && reportData.summary.totalSalespersonCommission !== undefined && (
            <>
              <div 
                className="rounded-lg shadow p-4 lg:p-6"
                style={{ backgroundColor: theme.cardBG || '#ffffff' }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faUsers} 
                    className="text-xl lg:text-2xl mr-3"
                    style={{ color: '#8b5cf6' }}
                  />
                  <div>
                    <div 
                      className="text-xl lg:text-2xl font-bold"
                      style={{ color: theme.textPrimary || '#111827' }}
                    >
                      ${reportData.summary.totalSalespersonCommission.toFixed(2)}
                    </div>
                    <div 
                      className="text-sm"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Salesperson Commission
                    </div>
                  </div>
                </div>
              </div>
              
              <div 
                className="rounded-lg shadow p-4 lg:p-6"
                style={{ backgroundColor: theme.cardBG || '#ffffff' }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faChartLine} 
                    className="text-xl lg:text-2xl mr-3"
                    style={{ color: '#10b981' }}
                  />
                  <div>
                    <div 
                      className="text-xl lg:text-2xl font-bold"
                      style={{ color: theme.textPrimary || '#111827' }}
                    >
                      ${reportData.summary.totalAffiliateProfit.toFixed(2)}
                    </div>
                    <div 
                      className="text-sm"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Affiliate Net Profit
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Commission Details Table */}
      <div 
        className="rounded-lg shadow p-4 lg:p-6"
        style={{ backgroundColor: theme.cardBG || '#ffffff' }}
      >
        <h2 
          className="text-lg lg:text-xl font-semibold mb-4"
          style={{ color: theme.textPrimary || '#111827' }}
        >
          Commission Details
        </h2>
        
        {reportData?.data && reportData.data.length > 0 ? (
          <>
            {/* Data scope indicator */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEye} className="text-blue-500 mr-2" />
                <div className="flex-1">
                  <span className="text-sm text-blue-700">
                    {isAffiliateAdmin 
                      ? `Showing commission data for ${selectedSalesperson || selectedReferralCode ? 'filtered' : 'all'} team referral codes (${reportData.summary.totalTransactions} transactions)`
                      : `Showing commission data for your ${selectedReferralCode ? 'filtered' : 'assigned'} referral codes (${reportData.summary.totalTransactions} transactions)`
                    }
                  </span>
                  {(selectedSalesperson || selectedReferralCode) && (
                    <div className="mt-1 flex flex-wrap gap-2">
                      {selectedSalesperson && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <FontAwesomeIcon icon={faUsers} className="mr-1" />
                          {selectedSalesperson}
                        </span>
                      )}
                      {selectedReferralCode && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <FontAwesomeIcon icon={faLink} className="mr-1" />
                          {selectedReferralCode}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  {isAffiliateAdmin && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referral Code
                    </th>
                  )}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Margin
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Affiliate %
                  </th>
                  {isAffiliateAdmin && (
                    <>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Salesperson %
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Commission
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Salesperson Comm.
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Affiliate Profit
                      </th>
                    </>
                  )}
                  {!isAffiliateAdmin && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.data.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(row.transactionDate).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      <div className="font-medium">{row.locationName}</div>
                      <div className="text-xs text-gray-400">
                        {row.locationCity}, {row.locationState}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {row.companyName}
                    </td>
                    {isAffiliateAdmin && (
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                        {row.referralCode}
                      </td>
                    )}
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.product}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.quantity.toFixed(2)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${row.marginTotal.toFixed(2)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {((row.affiliateCommissionRate || row.commissionPercentage / 100) * 100).toFixed(1)}%
                    </td>
                    {isAffiliateAdmin && (
                      <>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {((row.salespersonCommissionRate || 0) * 100).toFixed(1)}%
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          ${(row.totalCommissionEarned || row.commissionEarned).toFixed(2)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                          ${(row.salespersonCommissionEarned || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          ${(row.affiliateProfitEarned || row.commissionEarned).toFixed(2)}
                        </td>
                      </>
                    )}
                    {!isAffiliateAdmin && (
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        ${row.commissionEarned.toFixed(2)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FontAwesomeIcon icon={faChartLine} className="text-4xl mb-3 text-gray-300" />
            <p>No commission data available for the selected date range</p>
            <p className="text-sm">Try adjusting your date range or check back later</p>
          </div>
        )}
      </div>

      {/* Monthly Summary (if available) */}
      {summaryData?.monthlySummary && summaryData.monthlySummary.length > 0 && (
        <div 
          className="rounded-lg shadow p-4 lg:p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <h2 
            className="text-lg lg:text-xl font-semibold mb-4"
            style={{ color: theme.textPrimary || '#111827' }}
          >
            Monthly Summary (Last 12 Months)
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transactions
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customers
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Quantity
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Margin
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isAffiliateAdmin ? 'Total Commission' : 'Commission'}
                  </th>
                  {isAffiliateAdmin && (
                    <>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Salesperson Comm.
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Affiliate Profit
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summaryData.monthlySummary.map((month, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(month.Year, month.Month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {month.TransactionCount}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {month.UniqueCustomers}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(month.TotalQuantity).toFixed(2)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${parseFloat(month.TotalMargin).toFixed(2)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      ${parseFloat(month.TotalCommissionPaid || month.TotalCommission).toFixed(2)}
                    </td>
                    {isAffiliateAdmin && (
                      <>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                          ${parseFloat(month.TotalSalespersonCommission || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          ${parseFloat(month.TotalAffiliateProfit || month.TotalCommission).toFixed(2)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AffiliateReports;